// File Uploader Component for Feedback Attachments
// Supports drag & drop, paste from clipboard, and file selection
// Handles both images and documents (PDF, Word, Excel, etc.)

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  X,
  Upload,
  Image as ImageIcon,
  Loader2,
  AlertCircle,
  Clipboard,
  FileText,
  File as FileIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  uploadFeedbackAttachments,
  validateFile,
  getAttachmentConfig,
  formatFileSize,
  type FeedbackAttachment,
  type UploadProgress
} from '../services/feedbackStorage';

interface ImageUploaderProps {
  tenantId: string;
  userId: string;
  conversationId?: string;
  attachments: FeedbackAttachment[];
  onAttachmentsChange: (attachments: FeedbackAttachment[]) => void;
  disabled?: boolean;
  maxFiles?: number;
  className?: string;
}

// Helper to get icon for file type
const getFileIcon = (contentType: string) => {
  if (contentType.startsWith('image/')) return ImageIcon;
  if (contentType === 'application/pdf') return FileText;
  return FileIcon;
};

// Helper to check if file is an image
const isImageType = (contentType: string) => contentType.startsWith('image/');

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  tenantId,
  userId,
  conversationId,
  attachments,
  onAttachmentsChange,
  disabled = false,
  maxFiles,
  className
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const handleFilesRef = useRef<((files: File[]) => Promise<void>) | null>(null);

  const config = getAttachmentConfig();
  const effectiveMaxFiles = maxFiles ?? config.maxFilesPerMessage;
  const canAddMore = attachments.length < effectiveMaxFiles;

  // Handle paste from clipboard
  useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      if (disabled || !canAddMore || !handleFilesRef.current) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      const imageFiles: File[] = [];
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            // Name the pasted file with timestamp
            const ext = file.type.split('/')[1] || 'png';
            const namedFile = new File([file], `screenshot-${Date.now()}.${ext}`, {
              type: file.type
            });
            imageFiles.push(namedFile);
          }
        }
      }

      if (imageFiles.length > 0) {
        e.preventDefault();
        await handleFilesRef.current(imageFiles);
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [disabled, canAddMore]);

  const handleFiles = useCallback(
    async (files: File[]) => {
      if (!tenantId || !userId) {
        setErrors(['User context not available. Please try again.']);
        return;
      }

      // Validate all files
      const validFiles = files.filter(file => {
        const validation = validateFile(file);
        if (!validation.valid) {
          setErrors(prev => [...prev, `${file.name}: ${validation.error}`]);
          return false;
        }
        return true;
      });

      if (validFiles.length === 0) return;

      // Check if adding these would exceed limit
      const remainingSlots = effectiveMaxFiles - attachments.length;
      const filesToUpload = validFiles.slice(0, remainingSlots);

      if (filesToUpload.length < validFiles.length) {
        setErrors(prev => [
          ...prev,
          `Only ${remainingSlots} more file(s) can be added. ${validFiles.length - filesToUpload.length} file(s) skipped.`
        ]);
      }

      if (filesToUpload.length === 0) return;

      setIsUploading(true);
      setErrors([]);

      try {
        const result = await uploadFeedbackAttachments(
          tenantId,
          userId,
          filesToUpload,
          conversationId,
          progress => setUploadProgress([...progress])
        );

        // Add successful uploads to attachments
        if (result.successful.length > 0) {
          onAttachmentsChange([...attachments, ...result.successful]);
        }

        // Report failures
        if (result.failed.length > 0) {
          setErrors(result.failed.map(f => `${f.filename}: ${f.error}`));
        }
      } catch (error) {
        setErrors([error instanceof Error ? error.message : 'Upload failed']);
      } finally {
        setIsUploading(false);
        setUploadProgress([]);
      }
    },
    [tenantId, userId, conversationId, attachments, effectiveMaxFiles, onAttachmentsChange]
  );

  // Update ref whenever handleFiles changes
  useEffect(() => {
    handleFilesRef.current = handleFiles;
  }, [handleFiles]);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && canAddMore) {
        setIsDragging(true);
      }
    },
    [disabled, canAddMore]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled || !canAddMore) return;

      const files = Array.from(e.dataTransfer.files);
      handleFiles(files);
    },
    [disabled, canAddMore, handleFiles]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : [];
      if (files.length > 0) {
        handleFiles(files);
      }
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [handleFiles]
  );

  const handleRemoveAttachment = useCallback(
    (id: string) => {
      onAttachmentsChange(attachments.filter(a => a.id !== id));
    },
    [attachments, onAttachmentsChange]
  );

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  return (
    <div className={cn('space-y-3', className)}>
      {/* Drag & Drop Zone */}
      {canAddMore && (
        <div
          ref={dropZoneRef}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            'border-2 border-dashed rounded-lg p-4 transition-colors',
            'flex flex-col items-center justify-center gap-2 text-center',
            isDragging && 'border-primary bg-primary/5',
            !isDragging && 'border-muted-foreground/25 hover:border-muted-foreground/50',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          {isUploading ? (
            <div className='flex items-center gap-2 text-muted-foreground'>
              <Loader2 className='h-5 w-5 animate-spin' />
              <span className='text-sm'>Uploading...</span>
            </div>
          ) : (
            <>
              <div className='flex items-center gap-2 text-muted-foreground'>
                <Upload className='h-5 w-5' />
                <span className='text-sm'>Drop files here or</span>
                <Button
                  type='button'
                  variant='link'
                  size='sm'
                  className='h-auto p-0 text-sm'
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled}
                >
                  browse
                </Button>
              </div>
              <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                <Clipboard className='h-3 w-3' />
                <span>You can also paste screenshots from clipboard (Ctrl/Cmd+V)</span>
              </div>
              <p className='text-xs text-muted-foreground'>
                {config.acceptedExtensions} • Max {config.maxFileSizeMB}MB per file •{' '}
                {effectiveMaxFiles - attachments.length} of {effectiveMaxFiles} remaining
              </p>
            </>
          )}
        </div>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type='file'
        accept={config.acceptedTypes.join(',')}
        multiple
        className='hidden'
        onChange={handleFileSelect}
        disabled={disabled || !canAddMore}
      />

      {/* Upload Progress */}
      {uploadProgress.length > 0 && (
        <div className='space-y-2'>
          {uploadProgress.map((p, idx) => (
            <div key={idx} className='flex items-center gap-2 text-sm'>
              <div className='flex-1 bg-muted rounded-full h-2 overflow-hidden'>
                <div
                  className={cn(
                    'h-full transition-all duration-300',
                    p.status === 'error' ? 'bg-destructive' : 'bg-primary'
                  )}
                  style={{ width: `${p.progress}%` }}
                />
              </div>
              <span className='text-xs text-muted-foreground w-20 truncate'>{p.filename}</span>
            </div>
          ))}
        </div>
      )}

      {/* Error Messages */}
      {errors.length > 0 && (
        <div className='bg-destructive/10 border border-destructive/20 rounded-lg p-3'>
          <div className='flex items-start gap-2'>
            <AlertCircle className='h-4 w-4 text-destructive mt-0.5 shrink-0' />
            <div className='flex-1 space-y-1'>
              {errors.map((error, idx) => (
                <p key={idx} className='text-sm text-destructive'>
                  {error}
                </p>
              ))}
            </div>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-6 w-6 p-0'
              onClick={clearErrors}
            >
              <X className='h-4 w-4' />
            </Button>
          </div>
        </div>
      )}

      {/* Attachment Previews */}
      {attachments.length > 0 && (
        <div className='grid grid-cols-2 sm:grid-cols-3 gap-3'>
          {attachments.map(attachment => {
            const isImage = isImageType(attachment.content_type || '');
            const FileTypeIcon = getFileIcon(attachment.content_type || '');

            return (
              <div
                key={attachment.id}
                className={cn(
                  'relative group rounded-lg overflow-hidden border bg-muted',
                  isImage ? 'aspect-video' : 'aspect-square'
                )}
              >
                {isImage ? (
                  // Image preview
                  <img
                    src={attachment.url}
                    alt={attachment.original_filename}
                    className='w-full h-full object-cover'
                  />
                ) : (
                  // Document preview - show icon and filename
                  <a
                    href={attachment.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='w-full h-full flex flex-col items-center justify-center gap-2 p-3 hover:bg-accent transition-colors'
                  >
                    <FileTypeIcon className='h-10 w-10 text-muted-foreground' />
                    <span className='text-xs text-center text-muted-foreground line-clamp-2'>
                      {attachment.original_filename}
                    </span>
                  </a>
                )}
                {/* Overlay with filename (for images) */}
                {isImage && (
                  <div className='absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2'>
                    <p className='text-xs text-white truncate'>{attachment.original_filename}</p>
                    <p className='text-[10px] text-white/70'>
                      {formatFileSize(attachment.size)}
                      {attachment.width &&
                        attachment.height &&
                        ` • ${attachment.width}×${attachment.height}`}
                    </p>
                  </div>
                )}
                {/* File size for documents */}
                {!isImage && (
                  <div className='absolute inset-x-0 bottom-0 bg-muted border-t p-1 text-center'>
                    <p className='text-[10px] text-muted-foreground'>
                      {formatFileSize(attachment.size)}
                    </p>
                  </div>
                )}
                {/* Remove Button */}
                {!disabled && (
                  <button
                    type='button'
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveAttachment(attachment.id);
                    }}
                    className={cn(
                      'absolute top-2 right-2 p-1 rounded-full',
                      'bg-black/50 hover:bg-black/70 text-white',
                      'opacity-0 group-hover:opacity-100 transition-opacity'
                    )}
                  >
                    <X className='h-4 w-4' />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Max Files Reached Message */}
      {!canAddMore && (
        <p className='text-xs text-muted-foreground text-center'>
          Maximum {effectiveMaxFiles} attachments reached. Remove an attachment to add more.
        </p>
      )}
    </div>
  );
};

// Compact version for message composer
export const CompactImageUploader: React.FC<ImageUploaderProps> = props => {
  const { disabled, attachments, onAttachmentsChange } = props;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const config = getAttachmentConfig();
  const maxFiles = props.maxFiles ?? config.maxFilesPerMessage;
  const canAddMore = attachments.length < maxFiles;

  return (
    <div className='flex items-center gap-2'>
      <Button
        type='button'
        variant='ghost'
        size='sm'
        className='h-8 w-8 p-0'
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled || !canAddMore}
        title={canAddMore ? 'Attach image' : `Maximum ${maxFiles} images`}
      >
        <ImageIcon className='h-4 w-4' />
      </Button>
      <input
        ref={fileInputRef}
        type='file'
        accept={config.acceptedTypes.join(',')}
        multiple
        className='hidden'
        onChange={e => {
          // Reuse the full uploader logic by rendering it invisibly
          // For now, show a simple version
        }}
        disabled={disabled || !canAddMore}
      />
      {attachments.length > 0 && (
        <span className='text-xs text-muted-foreground'>
          {attachments.length} image{attachments.length !== 1 ? 's' : ''} attached
        </span>
      )}
    </div>
  );
};

export default ImageUploader;
